import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Users, ShieldCheck, ShieldX, Search, ChevronLeft, ChevronRight, LogIn, Lock, LockOpen } from 'lucide-react'
import { Button } from '@/components/ui/button'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'

interface User {
  id: number
  email: string
  role: string
  dashboardAccess: boolean
  createdAt: string
}

interface Pagination {
  page: number
  totalPages: number
  total: number
}

interface Props {
  users: User[]
  pagination: Pagination
  search: string
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function UsersIndex({ users, pagination, search }: Props) {
  const [query, setQuery] = useState(search)

  const toggleAccess = (user: User) => {
    router.patch(`/cc/users/${user.id}`, {
      dashboard_access: !user.dashboardAccess,
      search,
      page: pagination.page,
    })
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get('/cc/users', { search: query }, { preserveState: true })
  }

  const goToPage = (page: number) => {
    router.get('/cc/users', { search, page }, { preserveState: true })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-foreground" />
          <h2 className="text-2xl font-bold text-foreground">Usuarios</h2>
          <span className="text-sm text-muted-foreground">({pagination.total})</span>
        </div>

        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por email..."
              className="pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring w-64"
            />
          </div>
          <Button type="submit" variant="outline" size="sm">
            Buscar
          </Button>
        </form>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-220px)]">
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr className="bg-muted border-b border-border">
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">ID</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Criado em</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-accent transition-colors">
                  <td className="px-4 py-3 text-xs text-muted-foreground font-mono">{user.id}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground">{user.email}</span>
                      {user.role === 'admin' && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400">
                          admin
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(user.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {user.dashboardAccess || user.role === 'admin' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400">
                          <ShieldCheck className="h-3 w-3" />
                          Liberado
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-yellow-500/15 dark:text-yellow-400">
                          <ShieldX className="h-3 w-3" />
                          Bloqueado
                        </span>
                      )}
                      {user.role !== 'admin' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => toggleAccess(user)}
                          title={user.dashboardAccess ? 'Bloquear' : 'Liberar'}
                        >
                          {user.dashboardAccess ? <Lock className="h-3 w-3" /> : <LockOpen className="h-3 w-3" />}
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {user.role !== 'admin' && (
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" asChild>
                        {/* Full page navigation intentional: impersonate terminates the admin session */}
                        <a href={`/users/${user.id}/impersonate`}>
                          <LogIn className="h-3 w-3 mr-1" />
                          Impersonar
                        </a>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    Nenhum usuario encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted">
            <span className="text-sm text-muted-foreground">
              Pagina {pagination.page} de {pagination.totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => goToPage(pagination.page - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => goToPage(pagination.page + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

UsersIndex.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>

export default UsersIndex
